import { type FC, useState, useEffect, useCallback } from "react";
import { usePouch } from "use-pouchdb";
import { Link } from "react-router";

import { assert } from "../utils";
import { loadAllConflicts, type ConflictedDoc } from "../conflicts";

export const ConflictsPage: FC = () => {
  const db = usePouch();
  const [conflicts, setConflicts] = useState<ConflictedDoc[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [opErr, setOpErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setOpErr(null);
    try {
      setConflicts(await loadAllConflicts(db));
    } catch (e) {
      setOpErr(`${e}`);
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => { void reload(); }, [reload]);

  const keepVersion = useCallback(async (conflicted: ConflictedDoc, keepRev: string) => {
    setOpErr(null);
    try {
      if (keepRev !== conflicted.winnerRev) {
        // Promote chosen conflict revision: overwrite winner content, then delete all conflict revs
        const keepIdx = conflicted.conflictRevs.indexOf(keepRev);
        const keepDoc = conflicted.conflictDocs[keepIdx];
        assert(keepDoc != null, 'keepDoc missing');
        await db.put({ ...keepDoc, _rev: conflicted.winnerRev });
        // The old keepRev is now a conflict itself — delete it and all others
        await Promise.all(conflicted.conflictRevs.map(rev => db.remove(conflicted.id, rev)));
      } else {
        // Keep winner: delete all conflicting revisions
        await Promise.all(conflicted.conflictRevs.map(rev => db.remove(conflicted.id, rev)));
      }
      await reload();
    } catch (e) {
      setOpErr(`Failed to resolve: ${e}`);
    }
  }, [db, reload]);

  return (
    <div style={{ padding: '6px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <h3 style={{ margin: 0 }}>Conflicts</h3>
        <button type="button" onClick={() => void reload()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {opErr && <p style={{ color: 'red' }}>{opErr}</p>}
      {conflicts === null && !opErr && <p>Loading…</p>}
      {conflicts !== null && conflicts.length === 0 && <p style={{ color: 'green' }}>No conflicts.</p>}
      {conflicts !== null && conflicts.map(c => (
        <div key={c.id} style={{ border: '1px solid #aaa', borderRadius: '4px', margin: '8px 0', padding: '8px' }}>
          <p style={{ margin: '0 0 6px 0' }}>
            <strong>Doc:</strong> <Link to={`/items/${c.id}`}>{c.id}</Link>
            &nbsp;({1 + c.conflictRevs.length} conflicting versions)
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {[
              { rev: c.winnerRev, doc: c.winnerDoc, label: 'Current winner' },
              ...c.conflictRevs.map((rev, i) => ({ rev, doc: c.conflictDocs[i]!, label: `Conflict ${i + 1}` })),
            ].map(({ rev, doc, label }) => (
              <div key={rev} style={{ flex: '1 1 280px', border: '1px solid #ccc', borderRadius: '4px', padding: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <strong>{label}</strong>
                  <button type="button" onClick={() => void keepVersion(c, rev)}>Keep this</button>
                </div>
                <pre style={{ margin: 0, fontSize: '11px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#f5f5f5', padding: '4px', borderRadius: '2px' }}>
                  {JSON.stringify(doc, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
